import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Button,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Pagination,
  Alert,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Stack
} from '@mui/material';
import {
  FilterList,
  Sort,
  Search,
  Favorite,
  Visibility
} from '@mui/icons-material';

interface Animal {
  id: string;
  name: string;
  species: string;
  breed?: string;
  age?: number;
  size?: 'small' | 'medium' | 'large';
  sex?: 'male' | 'female';
  sterilized?: boolean;
  vaccinated?: boolean;
  status: 'available' | 'reserved' | 'adopted';
  description?: string;
  photos?: string[];
  created_at: string;
  shelter_intake_date?: string;
}

interface ShelterAnimalsProps {
  shelter: {
    id: string;
    name: string;
    animal_count?: number;
  };
}

const ShelterAnimals: React.FC<ShelterAnimalsProps> = ({ shelter }) => {
  const { shelterId } = useParams<{ shelterId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // State pentru animale
  const [animals, setAnimals] = useState<Animal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Filtre
  const [filters, setFilters] = useState({
    species: searchParams.get('species') || '',
    breed: searchParams.get('breed') || '',
    age: searchParams.get('age') || '',
    size: searchParams.get('size') || '',
    sex: searchParams.get('sex') || '',
    sterilized: searchParams.get('sterilized') || '',
    vaccinated: searchParams.get('vaccinated') || '',
    status: searchParams.get('status') || 'available',
    sort: searchParams.get('sort') || 'recent',
    search: searchParams.get('search') || ''
  });

  // Debounce pentru search
  const [searchDebounce, setSearchDebounce] = useState(filters.search);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounce(filters.search);
    }, 250);
    return () => clearTimeout(timer);
  }, [filters.search]);

  // Fetch animale
  useEffect(() => {
    const fetchAnimals = async () => {
      if (!shelterId) return;
      
      try {
        setLoading(true);
        const params = new URLSearchParams();
        
        // Adaugă filtrele la params
        Object.entries(filters).forEach(([key, value]) => {
          if (value) params.append(key, value);
        });
        
        params.append('page', page.toString());
        params.append('limit', '12');
        
        // API call real - rute publice, fără autentificare
        const response = await fetch(`/api/v1/shelters/${shelterId}/animals?${params.toString()}`, {
          headers: {
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const result = await response.json();
        
        if (result.success) {
          setAnimals(result.data.items);
          setTotal(result.data.total);
          setTotalPages(result.data.pages);
        } else {
          throw new Error(result.error || 'Eroare la încărcarea animalelor');
        }
        
      } catch (err: any) {
        setError(err.message || 'Eroare la încărcarea animalelor');
      } finally {
        setLoading(false);
      }
    };

    fetchAnimals();
  }, [shelterId, filters, page]);

  // Update URL când se schimbă filtrele
  useEffect(() => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.append(key, value);
    });
    setSearchParams(params);
  }, [filters, setSearchParams]);

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1); // Reset la prima pagină când se schimbă filtrele
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const getAgeLabel = (age?: number) => {
    if (!age) return 'Necunoscută';
    if (age < 1) return 'Pui';
    if (age < 3) return 'Junior';
    if (age < 7) return 'Adult';
    return 'Senior';
  };

  const getSizeLabel = (size?: string) => {
    switch (size) {
      case 'small': return 'Mică';
      case 'medium': return 'Medie';
      case 'large': return 'Mare';
      default: return 'Necunoscută';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">
          Animale disponibile ({total})
        </Typography>
        <Box display="flex" gap={1}>
          <Chip
            icon={<FilterList />}
            label="Filtre"
            color="primary"
            variant="outlined"
          />
          <Chip
            icon={<Sort />}
            label="Sortează"
            color="primary"
            variant="outlined"
          />
        </Box>
      </Box>

      {/* Filtre */}
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={2} flexWrap="wrap">
          <Box flex="1 1 200px">
            <TextField
              fullWidth
              label="Caută"
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              InputProps={{
                startAdornment: <Search />
              }}
            />
          </Box>
          
          <Box flex="1 1 200px">
            <FormControl fullWidth>
              <InputLabel>Specie</InputLabel>
              <Select
                value={filters.species}
                onChange={(e) => handleFilterChange('species', e.target.value)}
                label="Specie"
              >
                <MenuItem value="">Toate</MenuItem>
                <MenuItem value="dog">Câine</MenuItem>
                <MenuItem value="cat">Pisică</MenuItem>
                <MenuItem value="other">Altele</MenuItem>
              </Select>
            </FormControl>
          </Box>
          
          <Box flex="1 1 200px">
            <FormControl fullWidth>
              <InputLabel>Vârstă</InputLabel>
              <Select
                value={filters.age}
                onChange={(e) => handleFilterChange('age', e.target.value)}
                label="Vârstă"
              >
                <MenuItem value="">Toate</MenuItem>
                <MenuItem value="puppy">Pui</MenuItem>
                <MenuItem value="junior">Junior</MenuItem>
                <MenuItem value="adult">Adult</MenuItem>
                <MenuItem value="senior">Senior</MenuItem>
              </Select>
            </FormControl>
          </Box>
          
          <Box flex="1 1 200px">
            <FormControl fullWidth>
              <InputLabel>Sortează</InputLabel>
              <Select
                value={filters.sort}
                onChange={(e) => handleFilterChange('sort', e.target.value)}
                label="Sortează"
              >
                <MenuItem value="recent">Cele mai recente</MenuItem>
                <MenuItem value="seniority">Vechime în adapost</MenuItem>
                <MenuItem value="age">Vârstă</MenuItem>
                <MenuItem value="distance">Distanță</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Paper>

      {/* Grid animale */}
      {animals.length === 0 ? (
        <Paper elevation={1} sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Nu există animale disponibile momentan în acest adapost
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Verifică din nou mai târziu sau contactează adapostul pentru mai multe informații.
          </Typography>
        </Paper>
      ) : (
        <>
          <Box display="flex" flexWrap="wrap" gap={3}>
            {animals.map((animal) => (
              <Box key={animal.id} flex="1 1 300px" maxWidth="400px">
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardMedia
                    component="img"
                    height="200"
                    image={animal.photos?.[0] || 'https://via.placeholder.com/300x200'}
                    alt={animal.name}
                    sx={{ objectFit: 'cover' }}
                  />
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" gutterBottom>
                      {animal.name}
                    </Typography>
                    
                    <Box mb={2}>
                      <Chip 
                        label={`${animal.species} - ${animal.breed || 'Rasă necunoscută'}`}
                        size="small"
                        sx={{ mb: 1 }}
                      />
                      <Box display="flex" gap={1} flexWrap="wrap">
                        <Chip label={getAgeLabel(animal.age)} size="small" />
                        <Chip label={getSizeLabel(animal.size)} size="small" />
                        {animal.sterilized && <Chip label="Sterilizat" size="small" color="success" />}
                        {animal.vaccinated && <Chip label="Vaccinat" size="small" color="info" />}
                      </Box>
                    </Box>
                    
                    {animal.description && (
                      <Typography variant="body2" color="text.secondary">
                        {animal.description}
                      </Typography>
                    )}
                  </CardContent>
                  
                  <CardActions>
                    <Button 
                      size="small" 
                      startIcon={<Visibility />}
                      onClick={() => {
                        // TODO: Navigate to animal details
                        console.log('View animal details:', animal.id);
                      }}
                    >
                      Detalii
                    </Button>
                    <Button 
                      size="small" 
                      color="primary"
                      startIcon={<Favorite />}
                      onClick={() => {
                        // TODO: Handle adoption interest
                        console.log('Adoption interest:', animal.id);
                      }}
                    >
                      Adoptă
                    </Button>
                  </CardActions>
                </Card>
              </Box>
            ))}
          </Box>

          {/* Paginare */}
          {totalPages > 1 && (
            <Box display="flex" justifyContent="center" mt={4}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                size="large"
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default ShelterAnimals;
